import { z } from "zod";
import { AssetIdSchema, PositiveIntegerAmountSchema } from "./requests.js";

const orderIdentity = {
  // Converge derives child keys from this (`converge:<key>:pass:...`), and
  // stored converge keys cap at 256 characters.
  requestKey: z.string().trim().min(1).max(200),
  portfolioId: z.string().trim().min(1).max(256),
  /** The owner's Investing Account: funds buys, receives sale proceeds. */
  cashPortfolioId: z.string().trim().min(1).max(256),
};

/**
 * Buy: move the chosen settlement cash from the Investing Account into the
 * portfolio, then converge it to the portfolio's own strategy.
 */
const PortfolioBuyOrderSchema = z
  .object({
    side: z.literal("buy"),
    ...orderIdentity,
    legs: z
      .array(
        z
          .object({
            assetId: AssetIdSchema,
            amountRaw: PositiveIntegerAmountSchema,
          })
          .strict(),
      )
      .min(1)
      .max(8),
  })
  .strict();

/**
 * Sell: converge the portfolio to its strategy on a smaller base, raising
 * `amount` in settlement cash, then transfer the settled proceeds back to
 * the Investing Account on each chain. `all` exits the portfolio entirely.
 */
const PortfolioSellOrderSchema = z
  .object({
    side: z.literal("sell"),
    ...orderIdentity,
    amount: z.union([
      z
        .object({
          usd: z
            .string()
            .max(32)
            .regex(/^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/)
            .refine((value) => Number(value) > 0, "must be positive"),
        })
        .strict(),
      z.object({ all: z.literal(true) }).strict(),
    ]),
  })
  .strict();

/**
 * Owner-authorized portfolio order. The engine loads and compiles the
 * portfolio's own strategy; the caller only chooses the cash that moves.
 */
export const PortfolioOrderCreateSchema = z
  .discriminatedUnion("side", [PortfolioBuyOrderSchema, PortfolioSellOrderSchema])
  .superRefine((value, ctx) => {
    if (value.portfolioId === value.cashPortfolioId)
      ctx.addIssue({
        code: "custom",
        path: ["cashPortfolioId"],
        message: "Investing Account and portfolio must differ",
      });
    if (value.side !== "buy") return;
    const seen = new Set<string>();
    for (const [index, leg] of value.legs.entries()) {
      const key = leg.assetId.toLowerCase();
      if (!key.startsWith("eip155:") || seen.has(key))
        ctx.addIssue({
          code: "custom",
          path: ["legs", index],
          message: "Legs must be distinct EVM assets",
        });
      seen.add(key);
    }
  });
export type PortfolioOrderCreate = z.infer<typeof PortfolioOrderCreateSchema>;

const LiveDescriptorSchema = z
  .object({
    transport: z.literal("websocket"),
    path: z.string(),
    url: z.string(),
    requiresToken: z.boolean(),
    token: z.string().optional(),
    expiresAt: z.string().optional(),
  })
  .passthrough();

export const PortfolioOrderSchema = z.object({
  operationId: z.string().min(1),
  portfolioId: z.string().min(1),
  state: z.enum([
    "accepted",
    "running",
    "retrying",
    "awaiting_user",
    "completed",
    "failed",
    "cancelled",
  ]),
  headline: z.string(),
  detail: z.string().nullable(),
  phase: z.string().nullable(),
  pass: z.number().int().nullable(),
  maxPasses: z.number().int().nullable(),
  /**
   * How a COMPLETED order ended; null until then. `completed_with_drift`
   * means some value was held back (closed market, route limits, costly
   * bridge, a short sale): see `stopReasonCodes`, and `transferOut` for a
   * sell. Optional: additive to the original response.
   */
  outcome: z.enum(["converged", "completed_with_drift"]).nullable().optional(),
  /** Typed reasons behind `completed_with_drift` (e.g. `delivery_short`). */
  stopReasonCodes: z.array(z.string()).optional(),
  /**
   * Sells only, once the proceeds settled: what the owner asked for and what
   * reached the Investing Account, in USD at the settled hub price.
   * "Sold $transferredUsd of $requestedUsd". Null for buys and until then.
   */
  transferOut: z
    .object({
      requested: z.union([
        z.object({ usd: z.string() }),
        z.object({ all: z.literal(true) }),
      ]),
      requestedUsd: z.string(),
      plannedUsd: z.string(),
      transferredUsd: z.string(),
      shortfallUsd: z.string(),
    })
    .nullable()
    .optional(),
});
export type PortfolioOrder = z.infer<typeof PortfolioOrderSchema>;

export const PortfolioOrderCreatedSchema = PortfolioOrderSchema.extend({
  deduped: z.boolean(),
  portfolioLive: LiveDescriptorSchema,
});
export type PortfolioOrderCreated = z.infer<typeof PortfolioOrderCreatedSchema>;
